import { MotorCarriersService } from '@/@types/generated'
import { OperationType } from '@/@types/generated/models/OperationType'
import React, { createContext, useContext, useEffect, useState } from 'react'

interface OperationTypesContextProps {
  operationTypes: OperationType[]
}

const OperationTypesContext = createContext<OperationTypesContextProps>(
  undefined!
)

export function useOperationTypes() {
  const context = useContext(OperationTypesContext)
  if (context === undefined) {
    throw new Error(
      'useOperationTypes must be used within a OperationTypesContext.Provider route'
    )
  }

  return context
}

export function OperationTypesProvider({ children }: React.PropsWithChildren) {
  // State
  const [loading, setLoading] = useState(false)
  const [operationTypes, setOperationTypes] = useState<OperationType[]>([])

  // Load once
  useEffect(() => {
    const load = async () => {
      try {
        setOperationTypes(await getOperationTypes())
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  // No operation types yet, show loading
  if (loading) return <div>Retrieving operation types...</div>

  return (
    <OperationTypesContext.Provider value={{ operationTypes }}>
      {children}
    </OperationTypesContext.Provider>
  )
}

// Module-level cache and in-flight promise
const getOperationTypes: () => Promise<OperationType[]> = (() => {
  let cache: OperationType[] | null = null
  let inFlight: Promise<OperationType[]> | null = null

  return async () => {
    if (cache) return cache
    if (inFlight) return inFlight

    // Load from API
    inFlight = MotorCarriersService.listOperationTypes().then(
      ({ operationTypes }) => {
        cache = operationTypes || []
        inFlight = null
        return cache
      }
    )

    return inFlight
  }
})()
